import React from 'react';
import {
    Box,
    Button,
    Icon,
    Modal,
    Typography,
} from '@saddlebackchurch/react-cm-ui';
import makeStyles from '@saddlebackchurch/react-cm-ui/core/styles/makeStyles';
import {
    Trans,
    useTranslation,
} from 'react-i18next';
import { BEM_BLOCK_NAME } from './constants';

type PropTypes = {
    hasAnyRequiredFields: boolean;
    isOpen: boolean;
    legacyI18N?: (key: string) => string;
    shouldUseNewTranslations?: boolean;
    onClose: () => void;
};

const useStyles = makeStyles(({
    spacing,
}) => ({
    modalActionsMargin: {
        margin: [[spacing(5), spacing(4), 0]],
    },
    modalTitle: {
        marginTop: spacing(2),
    },
}));

function MissingRequiredFieldsModal({
    hasAnyRequiredFields,
    isOpen,
    legacyI18N,
    shouldUseNewTranslations,
    onClose,
}: PropTypes) {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <Modal
            isOpen={isOpen}
        >
            <Modal.Content
                alignItems="center"
            >
                <Icon
                    color="warning"
                    compact
                    size={64}
                    title={false}
                    type="exclamation"
                />

                {hasAnyRequiredFields ? (
                    <Typography
                        classes={{
                            root: classes.modalTitle,
                        }}
                        variant="h2"
                    >
                        {shouldUseNewTranslations ? t('requiredFieldsModal.missingRequiredFields.title') : legacyI18N('Fill in all required fields')}
                    </Typography>
                ) : (
                    <Typography
                        classes={{
                            root: classes.modalTitle,
                        }}
                        variant="h2"
                    >
                        {shouldUseNewTranslations ? t('requiredFieldsModal.noDataWhatsoever.title') : legacyI18N('Enter some data')}
                    </Typography>
                )}

                <Box mx={4}>
                    {hasAnyRequiredFields ? (
                        <Typography
                            align="center"
                            color="textSecondary"
                            variant="subtitle1"
                        >
                            {shouldUseNewTranslations ? (
                                <Trans i18nKey="requiredFieldsModal.missingRequiredFields.body" />
                            ) : (
                                <React.Fragment>
                                    {legacyI18N('Please fill in all fields marked with a red star *.')}
                                    <br />
                                    {legacyI18N('All other fields are optional.')}
                                </React.Fragment>
                            )}
                        </Typography>
                    ) : (
                        <Typography
                            align="center"
                            color="textSecondary"
                            variant="subtitle1"
                        >
                            {shouldUseNewTranslations ? (
                                <Trans i18nKey="requiredFieldsModal.noDataWhatsoever.body" />
                            ) : (
                                <React.Fragment>
                                    {legacyI18N('You must enter ')}
                                    <em>{legacyI18N('something')}</em>
                                    {' '}
                                    {legacyI18N('in order to submit this form.')}
                                    <br />
                                    {legacyI18N('It is not permitted to submit a completely empty form.')}
                                </React.Fragment>
                            )}
                        </Typography>
                    )}
                </Box>
            </Modal.Content>

            <Modal.Actions
                alignItems="center"
                classes={{
                    margin: classes.modalActionsMargin,
                }}
                direction="column"
            >
                <Button
                    color="secondary"
                    designVersion={2}
                    fullWidth
                    id={`${BEM_BLOCK_NAME}--missing_required_fields_modal_ok_button`}
                    onClick={onClose}
                    tabIndex={0}
                >
                    {shouldUseNewTranslations ? t('common.buttons.ok') : legacyI18N('OK')}
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

export default MissingRequiredFieldsModal;
